import { observable, action, computed, runInAction, when } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';

import { AppManager, Input, Variables, routerStore } from 'core/index';

import { FileMutation, CompleteFileMutation } from 'apps/file/gql';
import { clientStore } from 'apps/client/app';

import { analytics } from 'core/analytics';

import { FileApp, FileModel } from './app';

const DEFAULT_STORAGE_ERROR_MESSAGE =
  "Sorry we failed to upload the file '{{filename}}'";

export default class FileAppManager extends AppManager {
  static registryName = 'FileAppManager';
  /*
   * Architectural issues are notable here:
   * 1. When creating a new App the mutation yields a model but the created
   * App is disgarded as the "model" object is replaced with one with an id
   * (from the mutation).
   */

  /*
   * N.B. By design FileModel ends up being loaded in multiple trees
   * i.e. mapped to do different Apps
   */

  @observable fileModelCreated = false;
  @observable.ref localFile = null;
  @observable active = true;
  @observable sent = 0;
  @observable targetSpaceId = '';

  whenReadyToUploadDisposer = null;
  whenUploadedDisposer = null;
  whenCancelledDisposer = null;

  constructor(parent, init = {}) {
    super(parent, init);

    this.AppRef('file', FileApp);
    this.variables = new Variables({
      id: () => this.file && this.file.id,
    });

    if (!this.targetSpaceId) {
      if (this.formTarget?.inSpace) {
        this.targetSpaceId = this.formTarget.inSpace.id;
      } else {
        this.targetSpaceId = clientStore.signedInProfile?.inSpace.id;
      }
    }

    this.addFileInput = new Input({
      filename: () => this.file.filename,
      private: () => this.file.private,
      spaceId: () => (clientStore.signedInProfile ? this.targetSpaceId : null),
      contentType: () => this.file.mimeType, // contentType => mimeType?
      size: () => this.file.size,
      gitHash: () => this.file.gitHash,
      fileLastModified: () => this.file.fileLastModified,
      projectPath: () => this.projectPath,
    });
    this.fileMutation = new FileMutation(this.addFileInput);
    this.fileMutation.process(file => FileModel.Load(file), this.SetFile);
    /*
     * If mutations are used reusably like this,
     * we don't want to push the mutation itself but the change
     * The pattern has the disadvantage that concurrent calls
     * will be routed to the same callback
     */
  }

  @computed get uploadProgress() {
    if (this.file?.completed) {
      return 100;
    }
    if (this.file?.size) {
      return (Math.floor(this.sent) / this.file.size) * 100;
    }
    return 0;
  }

  @computed get uploadStatusText() {
    if (this.uploadProgress === 0) {
      return i18next.t('Initialising...');
    }
    if (this.uploadProgress && this.uploadProgress !== 100) {
      return `${Math.round(this.uploadProgress)}%`;
    }
    if (!this.file?.completed) {
      return i18next.t('Finalising...');
    }
    return '';
  }

  @computed get uploadStatusTextLong() {
    if (this.uploadProgress && this.uploadProgress !== 100) {
      return `Uploading — ${Math.round(this.uploadProgress)}%`;
    }
    return this.uploadStatusText;
  }

  @action SetActive(bool) {
    this.active = bool;
  }

  disposeWhen() {
    return (
      (!this.file && !this.active) ||
      (this.file &&
        (this.file.completed || this.file.cancelled) &&
        !this.active)
    );
  }

  dispose() {
    super.dispose();
  }

  @action SetFile = fileModel => {
    this.file = fileModel;
    if (this.localFile) this.file.SetLocalFileRef(this.localFile);
  };

  createFileModel(options) {
    const headers = {};
    if (!clientStore.signedInProfile && clientStore.HMAC) {
      headers['X-Wikifactory-Hmac'] = clientStore.HMAC;
    }
    this.fileMutation.addHeaders(headers);
    this.fileMutation.setCallbacks(options);
    this.fileMutation.mutate();
  }

  @action FileCreated = () => {
    this.fileModelCreated = true;
    if (this.file.isCopy || this.file.completed) return;
    this.doUpload({
      onComplete: this.CompleteUpload,
      onProgress: this.UpdateProgress,
      onUploadError: this.UploadError,
      onNetError: this.NetError,
      onAbort: this.CancelUpload,
    });
  };

  doUpload(options) {
    if (this.req && this.req.readyState !== 4) {
      this.req.abort();
    }
    const acl_header_value = this.file.private ? 'private' : 'public-read';

    this.req = new XMLHttpRequest();
    this.req.open('PUT', this.file.uploadUrl, true);
    this.req.setRequestHeader('x-amz-acl', acl_header_value);

    this.req.upload.addEventListener(
      'progress',
      ev => {
        if (ev.lengthComputable) {
          options.onProgress(ev);
        }
      },
      false,
    );
    this.req.onload = () => {
      if (this.req.status === 200) {
        options.onComplete(this.req.response);
      } else {
        options.onUploadError(this.req.response);
      }
    };
    this.req.onerror = options.onNetError;

    this.req.onabort = options.onAbort;
    this.req.send(this.localFile);
  }

  @action SetLocalFile(fileObj) {
    this.localFile = fileObj;
    this.file = new FileApp();
    this.file.SetLocalFile(fileObj);
    this.revokeWhenComplete();
    this.fileModelCreated = false;
  }

  @action SetHash(hash) {
    this.file.gitHash = hash;
  }

  @action SetUploadedCallback = callback => {
    this.uploadedCallback = callback;
  };

  @action SetUploadCancelledCallback = callback => {
    this.uploadCancelledCallback = callback;
  };

  @action SyncFile() {
    this.whenReadyToUploadDisposer?.();
    this.whenUploadedDisposer?.();
    this.whenCancelledDisposer?.();
    // Wait for spaceId to be set before creating the fileModel
    // This is not always the case, where we set the spaceId after setting the localFile.
    // e.g. StoryCreateForm
    // If we change the space.id begin upload again - forcing the previous upload to abort
    this.whenReadyToUploadDisposer = when(
      () => !clientStore.signedInProfile || this.targetSpaceId,
      () => {
        runInAction(() =>
          this.createFileModel({
            onSuccess: this.FileCreated,
            onUserErr: this.UserError,
            onSysErr: this.Error,
          }),
        );
      },
    );
    this.whenUploadedDisposer = when(
      () => this.file.completed,
      () => this.uploadedCallback(this.file),
    );
    this.whenCancelledDisposer = when(
      () => this.file.cancelled,
      () => this.uploadCancelledCallback(this.file),
    );
    this.disposers.push(this.whenReadyToUploadDisposer);
    this.disposers.push(this.whenUploadedDisposer);
    this.disposers.push(this.whenCancelledDisposer);
  }

  @action UserError = err => {
    const { filename } = this.file;
    if (err.extensions.code === 'BILLING_ERROR') {
      Alert.error(
        i18next.t(
          "Unable to upload '{{filename}}' due to storage limit exceeded. Please upgrade your plan or purchase an add-on and try again.",
          {
            filename,
          },
        ),
      );
      return;
    }
    Alert.error(
      i18next.t(DEFAULT_STORAGE_ERROR_MESSAGE, {
        filename,
      }),
    );
  };

  @action Error = err => {
    const { filename } = this.file;
    analytics.logError(err);
    Alert.error(
      i18next.t(DEFAULT_STORAGE_ERROR_MESSAGE, {
        filename,
      }),
    );
  };

  @action CompleteUpload = () => {
    const completeFileInput = new Input({
      id: () => this.file.id,
      completed: () => true,
    });
    const headers = {};
    if (!clientStore.signedInProfile && clientStore.HMAC) {
      headers['X-Wikifactory-Hmac'] = clientStore.HMAC;
    }
    const fileMutation = new CompleteFileMutation(
      completeFileInput,
      null,
      {},
      false,
      headers,
    );
    fileMutation.process(file => FileModel.Load(file), this.SetFile);
    const { route } = routerStore;
    const { diff, ...restParams } = route.params;

    fileMutation.mutate({
      onSuccess: () => {
        routerStore.navigate(route.name, restParams);
      },
      onUserErr: () => this.UserError,
      onSysErr: () => this.Error,
    });
  };

  @action NetError = () => {
    const { filename } = this.file;
    Alert.error(
      i18next.t("Network error while uploading the file '{{filename}}'.", {
        filename,
      }),
    );
    this.file.cancelled = true;
  };

  @action UploadError = resp => {
    const msg = resp && resp.msg ? resp.msg : '';
    const { filename } = this.file;
    Alert.error(
      i18next.t('The upload {{filename}} failed. {{msg}}', {
        filename,
        msg,
      }),
    );
    this.file.cancelled = true;
  };

  @action CancelUpload = () => {
    const { filename } = this.file;
    Alert.success(
      i18next.t('You have cancelled the upload of {{filename}}.', { filename }),
    );
    this.file.cancelled = true;
    // delete FileModel (nullify references)? Always possible?
  };

  @action UpdateProgress = ({ loaded }) => {
    this.sent = loaded;
  };

  @action Revoke = filename => {
    window.URL.revokeObjectURL(this.localFile.preview);
    // If the file that has completed is the current localFile
    //  => nullify the file so that the uploaded file can be displayed
    if (this.localFile.name === filename) {
      this.localFile = null;
    }
  };

  revokeWhenComplete() {
    this.disposers.push(() => {
      this.Revoke(this.localFile.name);
    });
  }
}
